import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import cx from 'classnames';
import styled from 'styled-components';
import { useUiValue } from 'stores/uiContext';
import CloseIcon from 'components/icons/Close';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { searchProductTypes } from 'utils/enums';
import useFavorites from 'utils/hooks/useFavorites';
import { useCountryValue } from 'stores/country';

import { IconButton, Button } from 'components/components/atoms';
import { CommunityDropdown } from 'components/components/molecules';
import { SearchResult } from 'components/components/organisms';

const StyledIconButton = styled(IconButton)`
  & svg {
    width: 0.75rem;
    width: 0.75rem;
  }
`;

const Title = styled.span`
  ${({ isMobile }) => (isMobile ? 'font-size: 1.75rem;' : 'font-size: 2.8rem;')}
  letter-spacing: -0.0075em;
`;

const PlanContainer = styled.div`
  ${({ isMobile }) => (isMobile ? 'width: 100%;' : 'width: 6.875rem;')}
`;
const QMIContainer = styled.div`
  ${({ isMobile }) => (isMobile ? 'width: 100%;' : 'width: 10.625rem;')}
`;

const LabelContainer = styled.div.attrs({
  className: 'h-full flex justify-center items-center text-sm text-center',
})`
  line-height: 1.125rem;
`;

const StyledButton = styled.button`
  height: 3.125rem;
`;

const ResultsButton = styled(Button)`
  height: 2.875rem;
  width: 10.625rem;
  & > div {
    margin-top: 0rem;
  }
`;

const Footer = styled.div`
  height: 4.375rem;
`;

const applyFilters = (array, selectedCommunity) =>
  !Array.isArray(array)
    ? []
    : array.filter((el) => {
        let community = true;
        if (selectedCommunity.length > 1) {
          for (let sC = 1; sC < selectedCommunity.length; sC++) {
            if (selectedCommunity[sC].type !== 'MPC') {
              const elementCommunity = el.idCommunity;
              community = elementCommunity === selectedCommunity[sC].id;
            } else {
              const elementMpc = el.idMpc;
              community = elementMpc === selectedCommunity[sC].id;
            }

            if (community) break;
          }
        }
        return community;
      });

const filterCards = (favoriteCards, productType, selectedCommunity) =>
  Object.entries(favoriteCards).reduce((accum, [key, community]) => {
    if (!['plan', 'qmi'].includes(productType)) {
      return accum;
    }
    if (selectedCommunity && selectedCommunity.length > 1) {
      const isCommunitySelected = selectedCommunity.filter(
        (c) => c.id === community.idCommunity,
      );
      if (isCommunitySelected.length > 0) {
        return [...accum, community];
      }
      return [...accum];
    }
    return [...accum, community];
  }, []);

const SearchFavorites = ({ fields, rendering }) => {
  const [{ notificationActive }] = useUiValue();
  const [{ country }] = useCountryValue();

  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const subHeading = getFieldValue(fields, 'subHeading');
  const mpcCards = getFieldValue(fields, 'mpcCards');
  const communityCards = getFieldValue(fields, 'communityCards');
  const planCards = getFieldValue(fields, 'planCards');
  const qmiCards = getFieldValue(fields, 'qmiCards');
  const mortgageAmounts = getFieldValue(fields, 'mortgageAmounts');

  const initialProductType = searchProductTypes.COMMUNITY;

  const [fixed, setFixed] = useState();
  const [productType, setProductType] = useState(initialProductType);
  const [productTypeTitle, setProductTypeTitle] = useState(
    getFieldValue(fields, 'headlineCommunity'),
  );
  const [selectedCommunity, setSelectedCommunity] = useState([
    { id: null, title: 'All' },
  ]);

  const searchResultRef = useRef(null);
  const [mobileFilters, setMobileFilters] = useState(false);

  const { favoritesList, saveFavorite, deleteFavorite } = useFavorites();
  useEffect(() => {
    if (productType === searchProductTypes.COMMUNITY) {
      setProductTypeTitle(getFieldValue(fields, 'headlineCommunity'));
    }
    if (productType === searchProductTypes.PLAN) {
      setProductTypeTitle(getFieldValue(fields, 'headlinePlan'));
    }
    if (productType === searchProductTypes.QMI) {
      setProductTypeTitle(getFieldValue(fields, 'headlineQMI'));
    }
  }, [productType, fields]);

  const cardsToShow = useMemo(() => {
    if (productType === searchProductTypes.COMMUNITY) {
      return communityCards.concat(mpcCards);
    }
    if (productType === searchProductTypes.QMI) return qmiCards;
    if (productType === searchProductTypes.PLAN) return planCards;
  }, [communityCards, planCards, qmiCards, mpcCards, productType]);

  const filters = [selectedCommunity];

  const favoriteCards = cardsToShow.filter((c) => favoritesList.includes(c.id));
  const favCommunityCards = communityCards
    .concat(mpcCards)
    .filter((c) => favoritesList.includes(c.id));
  const favQmiCards = qmiCards.filter((c) => favoritesList.includes(c.id));
  const favPlanCards = planCards.filter((c) => favoritesList.includes(c.id));
  const [nCommunity, setnCommunity] = useState(0);
  const [nPlan, setnPlan] = useState(0);
  const [nQMI, setnQMI] = useState(0);

  let filteredCards = useMemo(
    () =>
      productType === searchProductTypes.COMMUNITY
        ? applyFilters(Object.values(favoriteCards), ...filters)
        : filterCards(favoriteCards, productType, selectedCommunity),
    [productType, selectedCommunity, favoriteCards],
  );

  const handleCommunityChange = (community) => {
    if (community.id) {
      const listOfSelectedCommunities = [...selectedCommunity];
      const currentPos = listOfSelectedCommunities.findIndex((c) => {
        return c.id === community.id;
      });

      if (currentPos > 0) {
        if (community.type === 'Community') {
          if (community.idMpc) {
            listOfSelectedCommunities.splice(currentPos, 1);
            const comunityMPC = listOfSelectedCommunities.findIndex(
              (c) => c.id === community.idMpc,
            );
            if (comunityMPC > 0) {
              listOfSelectedCommunities.splice(comunityMPC, 1);
            }
          } else {
            listOfSelectedCommunities.splice(currentPos, 1);
          }
        } else {
          listOfSelectedCommunities.splice(currentPos, 1);
          const communitiesofMPC = listOfSelectedCommunities.filter(
            (c) => c.idMpc === community.id,
          );
          for (let i = 0; i < communitiesofMPC.length; i++) {
            const communityIndex = listOfSelectedCommunities.findIndex((c) => {
              return c.id === communitiesofMPC[i].id;
            });
            if (communityIndex > 0) {
              listOfSelectedCommunities.splice(communityIndex, 1);
            }
          }
        }
      } else {
        if (community.type === 'Community') {
          listOfSelectedCommunities.push(community);
        } else {
          listOfSelectedCommunities.push(community);
          const communitiesofMPC = communityCards.filter(
            (c) => c.idMpc === community.id,
          );
          for (let i = 0; i < communitiesofMPC.length; i++) {
            const communityExits = listOfSelectedCommunities.findIndex((c) => {
              return c.id === communitiesofMPC[i].id;
            });
            if (communityExits <= 0) {
              listOfSelectedCommunities.push(communitiesofMPC[i]);
            }
          }
        }
      }
      setSelectedCommunity(listOfSelectedCommunities);
      setnCommunity(listOfSelectedCommunities.length);
    } else {
      setSelectedCommunity([{ id: null, title: 'All' }]);
      setnCommunity(favCommunityCards.length);
    }
  };

  useEffect(() => {
    setnCommunity(favCommunityCards.length);
    if (selectedCommunity && selectedCommunity.length > 1) {
      let qmis = 0;
      let plans = 0;
      for (let sC = 1; sC < selectedCommunity.length; sC++) {
        qmis =
          qmis +
          favQmiCards.filter((c) =>
            selectedCommunity[sC].type === 'MPC'
              ? selectedCommunity[sC].idMpc === c.idMpc
              : selectedCommunity[sC].idCommunity === c.idCommunity,
          ).length;
        plans =
          plans +
          favPlanCards.filter((c) =>
            selectedCommunity[sC].type === 'MPC'
              ? selectedCommunity[sC].idMpc === c.idMpc
              : selectedCommunity[sC].idCommunity === c.idCommunity,
          ).length;
      }
      setnPlan(plans);
      setnQMI(qmis);
    } else {
      setnPlan(favPlanCards.length);
      setnQMI(favQmiCards.length);
    }
  }, [selectedCommunity, fields, favoritesList]);

  const handlePrimaryFilterChange = (product) => {
    setProductType(product);
    if (product === searchProductTypes.COMMUNITY) {
      setProductTypeTitle(getFieldValue(fields, 'headlineCommunity'));
    }
    if (product === searchProductTypes.PLAN) {
      setProductTypeTitle(getFieldValue(fields, 'headlinePlan'));
    }
    if (product === searchProductTypes.QMI) {
      setProductTypeTitle(getFieldValue(fields, 'headlineQMI'));
    }
  };

  const clearFilters = useCallback(() => {
    setSelectedCommunity([{ id: null, title: 'All' }]);
  }, [fields]);

  const handleOnScroll = () => {
    if (!isMobile) {
      const headerOffset = notificationActive ? 170 : 110;
      if (window.pageYOffset > headerOffset) {
        setFixed(true);
      } else {
        setFixed(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  return (
    <section
      id='FavoritesSearch'
      role='search'
      className='w-full flex flex-col'
      tabIndex={-1}
    >
      {fixed && <div className='h-20'></div>}
      {isMobile ? (
        <div className='w-screen top-0 flex bg-white items-center relative'>
          <div
            className={cx('w-full flex items-center', {
              'top-0 h-screen pt-4 z-50': mobileFilters,
              'px-5 h-20': !mobileFilters,
            })}
          >
            {mobileFilters ? (
              <div className='fixed top-0 left-0 w-screen h-screen flex flex-col justify-between bg-white'>
                {isMobile && (
                  <div
                    className='relative flex w-full mt-6 mb-5 items-center justify-center text-center
                  text-action-blue font-trade-gothic-20 text-2xl'
                  >
                    <span>More Filters</span>
                    <div onClick={() => setMobileFilters(false)}>
                      <CloseIcon className='absolute right-0 top-0 bottom-0 m-auto w-6 mr-5' />
                    </div>
                  </div>
                )}
                <div className='px-5 pt-5 flex-grow'>
                  {(!isMobile || mobileFilters) && (
                    <div className='w-full md:w-11/12 xl:w-1/2 h-full lg:mr-20 flex flex-col md:flex-row items-center md:justify-start relative'>
                      <div className='w-full md:w-auto mb-5 md:mb-0 md:mr-5'>
                        <CommunityDropdown
                          onCommunityChange={handleCommunityChange}
                          selectedCommunity={selectedCommunity}
                          selectedProductType={productType}
                          communitiesFilter={favCommunityCards}
                          onPrimaryFilterChange={handlePrimaryFilterChange}
                          nCommunity={nCommunity}
                        />
                      </div>
                      <QMIContainer
                        isMobile={isMobile}
                        className='mb-5 md:mb-0 md:mr-5'
                      >
                        <StyledButton
                          className={cx(
                            'font-graphie px-4 cursor-pointer rounded-lg border border-action-blue',
                            'md:border-none transition transition-all duration-300 hover:text-white hover:bg-action-blue',
                            {
                              'text-white bg-action-blue':
                                productType === 'qmi',
                              'bg-white text-action-blue':
                                productType !== 'qmi',
                            },
                          )}
                          onClick={() => {
                            handlePrimaryFilterChange(searchProductTypes.QMI);
                          }}
                          role='button'
                          aria-label={
                            nQMI > 0
                              ? `Quick Move-Ins ${
                                  nQMI || 0
                                }. Results will filter on the page.`
                              : 'All Quick Move-Ins. No results found.'
                          }
                        >
                          <LabelContainer>
                            Quick Move-Ins{' '}
                            <span className='ml-2'>{nQMI || 0}</span>
                          </LabelContainer>
                        </StyledButton>
                      </QMIContainer>
                      <PlanContainer
                        isMobile={isMobile}
                        className='mb-5 md:mb-0'
                      >
                        <StyledButton
                          className={cx(
                            'font-graphie px-4 cursor-pointer rounded-lg border border-action-blue',
                            'md:border-none transition transition-all duration-300 hover:text-white hover:bg-action-blue',
                            {
                              'text-white bg-action-blue':
                                productType === 'plan',
                              'bg-white text-action-blue':
                                productType !== 'plan',
                            },
                          )}
                          onClick={() => {
                            handlePrimaryFilterChange(searchProductTypes.PLAN);
                          }}
                          role='button'
                          aria-label={
                            nPlan > 0
                              ? `Plans ${
                                  nPlan || 0
                                }. Results will filter on the page.`
                              : 'All Plans. No results found.'
                          }
                        >
                          <LabelContainer>
                            Plans <span className='ml-2'>{nPlan || 0}</span>
                          </LabelContainer>
                        </StyledButton>
                      </PlanContainer>
                    </div>
                  )}
                </div>
                <Footer className='w-full pr-5 pl-10 py-3 bg-action-blue flex justify-between items-center fixed bottom-0'>
                  <p
                    className='font-graphie text-base underline text-white'
                    onClick={clearFilters}
                  >
                    {'Clear all'}
                  </p>
                  <ResultsButton
                    onClick={() => setMobileFilters(false)}
                    color='white'
                    variant='filled'
                    className='pt-0 pb-0'
                  >
                    <p className='text-sm mt-1 normal-case'>{`Show ${filteredCards.length} results`}</p>
                  </ResultsButton>
                </Footer>
              </div>
            ) : (
              <div className='w-full h-full flex justify-end items-center'>
                <StyledButton
                  className='w-full flex items-center bg-action-blue text-white rounded-lg text-center focus:outline-none'
                  onClick={() => setMobileFilters(true)}
                >
                  <p className='w-full'>{'Filters'}</p>
                </StyledButton>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          className={cx(
            'w-full md:w-screen top-0 flex bg-white md:bg-mattamy-blue-light items-center justify-between z-30 md:h-20',
            {
              fixed,
            },
          )}
        >
          <div className='w-full md:h-20 md:px-10 md:py-4 flex items-center md:bg-light-blue'>
            {(!isMobile || mobileFilters) && (
              <div className='w-full md:w-11/12 xl:w-1/2 h-full lg:mr-20 flex flex-col md:flex-row items-center md:justify-start relative'>
                <div className='w-full md:w-auto mb-5 md:mb-0 md:mr-5'>
                  <CommunityDropdown
                    onCommunityChange={handleCommunityChange}
                    selectedCommunity={selectedCommunity}
                    selectedProductType={productType}
                    communitiesFilter={favCommunityCards}
                    onPrimaryFilterChange={handlePrimaryFilterChange}
                    nCommunity={nCommunity}
                    dropdownDescription={
                      nCommunity > 0
                        ? 'Results will filter on the page.'
                        : 'No results found.'
                    }
                  />
                </div>
                <QMIContainer
                  isMobile={isMobile}
                  className='mb-5 md:mb-0 md:mr-5'
                >
                  <StyledButton
                    className={cx(
                      'font-graphie px-4 cursor-pointer rounded-lg border border-action-blue',
                      'md:border-none transition transition-all duration-300 hover:text-white hover:bg-action-blue',
                      {
                        'text-white bg-action-blue': productType === 'qmi',
                        'text-action-blue bg-white': productType !== 'qmi',
                      },
                    )}
                    onClick={() => {
                      handlePrimaryFilterChange(searchProductTypes.QMI);
                    }}
                    aria-label={
                      nQMI > 0
                        ? `Quick Move-Ins ${
                            nQMI || 0
                          }. Results will filter on the page.`
                        : 'All Quick Move-Ins. No results found.'
                    }
                  >
                    <LabelContainer>
                      Quick Move-Ins <span className='ml-2'>{nQMI || 0}</span>
                    </LabelContainer>
                  </StyledButton>
                </QMIContainer>
                <PlanContainer isMobile={isMobile} className='mb-5 md:mb-0'>
                  <StyledButton
                    className={cx(
                      'font-graphie px-4 cursor-pointer rounded-lg border border-action-blue',
                      'md:border-none transition transition-all duration-300 hover:text-white hover:bg-action-blue',
                      {
                        'text-white bg-action-blue': productType === 'plan',
                        'text-action-blue bg-white ': productType !== 'plan',
                      },
                    )}
                    onClick={() => {
                      handlePrimaryFilterChange(searchProductTypes.PLAN);
                    }}
                    aria-label={
                      nPlan > 0
                        ? `Plans ${
                            nPlan || 0
                          }. Results will filter on the page.`
                        : 'All Plans. No results found.'
                    }
                  >
                    <LabelContainer>
                      Plans <span className='ml-2'>{nPlan || 0}</span>
                    </LabelContainer>
                  </StyledButton>
                </PlanContainer>
              </div>
            )}
          </div>
        </div>
      )}
      <div className='flex flex-col w-full px-5 md:px-10 pb-10 md:pb-0'>
        {filteredCards.length > 0 && (
          <>
            <div className='pt-6 md:pt-10 flex'>
              <button
                className='flex items-center cursor-pointer'
                //href='#' //'javascript:history.back()'
                aria-label='Back'
                onClick={(e) => {
                  e.preventDefault();
                  if (typeof window !== 'undefined') window.history.back();
                }}
              >
                <StyledIconButton
                  icon='arrow-left'
                  idIcon={'SearchFavoritesBack'}
                  circled
                  color='aqua'
                  small
                  hoverState
                  buttonLabel={'Favorites Back button'}
                  title={'Back icon'}
                />
                <span className='ml-5 mt-1 text-mattamy-blue uppercase font-trade-gothic-20'>
                  {'back'}
                </span>
              </button>
            </div>
            <Title
              className='text-mattamy-blue font-trade-gothic-20 mt-6 md:mt-12 md:leading-9'
              isMobile={isMobile}
            >
              {productTypeTitle}
            </Title>
            <span className='hidden md:block text-action-blue font-graphie mt-2 tracking-wider'>
              {subHeading}
            </span>
          </>
        )}
        {filteredCards && (
          <div
            className='flex-grow overflow-visible'
            // data-aos="fade-in-up"
            // data-aos-duration="1000"
            // data-aos-delay={!isMobile ? "200" : "0"}
          >
            <SearchResult
              cards={filteredCards}
              gridColumns={5}
              noResults={getFieldValue(fields, 'noResults')}
              productType={productType}
              containerRef={searchResultRef}
              noAvailableNumber={!isMobile}
              noPadding
              noPaddingLeftNoResults
              favoritesScreen
              rendering={rendering}
              saveFavorite={saveFavorite}
              deleteFavorite={deleteFavorite}
              favoritesList={favoritesList}
              country={country}
              mortgageAmounts={mortgageAmounts}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default SearchFavorites;
